import { MediaPreview } from "@/components/studio/file-upload";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  THUMBNAIL_ACCEPT_ATTR,
  THUMBNAIL_HINT,
} from "@/lib/thumbnail-validation";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

interface VariantData {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  aboutLongDescription: string;
  localizedIngredients: string[];
  localizedSteps: string[];
  userTips: string[];
  thumbnailUrl: string | null;
  prepTimeMinutes: number | null;
  cookTimeMinutes: number | null;
}

// EVO-20: recipes saved with no category/title, submitted with no
// ingredients or steps, and accepted negative or decimal minutes.
export function recipeFormError(
  categoryId: string,
  variants: VariantData[],
  forSubmit: boolean,
): string | null {
  if (!categoryId) return "Select a category.";
  for (const v of variants) {
    const lang = v.languageCode.toUpperCase();
    if (!v.localizedTitle?.trim()) return `Add a title (${lang}).`;
    for (const [label, m] of [
      ["Prep", v.prepTimeMinutes],
      ["Cook", v.cookTimeMinutes],
    ] as const) {
      if (m != null && (!Number.isInteger(m) || m < 0))
        return `${label} time must be a whole number of minutes, 0 or more (${lang}).`;
    }
    if (!forSubmit) continue;
    if (!v.localizedIngredients.some((s) => s.trim()))
      return `Add at least one ingredient (${lang}).`;
    if (!v.localizedSteps.some((s) => s.trim()))
      return `Add at least one step (${lang}).`;
  }
  return null;
}

interface RecipeVariantFieldsProps {
  variant: VariantData;
  onFieldChange: (field: keyof VariantData, value: any) => void;
  onFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => void;
  isUploading: boolean;
  isReadOnly?: boolean;
}

interface SortableStepRowProps {
  id: string;
  index: number;
  value: string;
  disabled: boolean;
  testIdPrefix: string;
  onUpdate: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

function SortableStepRow({
  id,
  index,
  value,
  disabled,
  testIdPrefix,
  onUpdate,
  onRemove,
}: SortableStepRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2"
      data-testid={`${testIdPrefix}-row-${index}`}
    >
      <div
        className="flex flex-col shrink-0 cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
        style={{ visibility: disabled ? "hidden" : "visible" }}
        data-testid={`${testIdPrefix}-drag-handle-${index}`}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <span className="text-xs text-muted-foreground w-5 shrink-0">
        {index + 1}.
      </span>
      <Input
        value={value}
        onChange={(e) => onUpdate(index, e.target.value)}
        disabled={disabled}
        className="flex-1"
        data-testid={`${testIdPrefix}-item-${index}`}
      />
      {!disabled && (
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(index)}
          data-testid={`${testIdPrefix}-remove-${index}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function SortableStepsList({
  label,
  items,
  onChange,
  placeholder,
  disabled,
  testIdPrefix,
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  disabled: boolean;
  testIdPrefix: string;
}) {
  const [newItem, setNewItem] = useState("");

  const itemIds = items.map((_, i) => `${testIdPrefix}-${i}`);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setNewItem("");
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = itemIds.indexOf(active.id as string);
    const newIndex = itemIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {items.map((item, index) => (
              <SortableStepRow
                key={itemIds[index]}
                id={itemIds[index]}
                index={index}
                value={item}
                disabled={disabled}
                testIdPrefix={testIdPrefix}
                onUpdate={updateItem}
                onRemove={removeItem}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!disabled && (
        <div className="flex items-center gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addItem();
              }
            }}
            data-testid={`${testIdPrefix}-new-input`}
          />
          <Button
            size="icon"
            variant="outline"
            onClick={addItem}
            disabled={!newItem.trim()}
            data-testid={`${testIdPrefix}-add`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

function SimpleListEditor({
  label,
  items,
  onChange,
  placeholder,
  disabled,
  testIdPrefix,
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  disabled: boolean;
  testIdPrefix: string;
}) {
  const [newItem, setNewItem] = useState("");

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setNewItem("");
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="space-y-1.5">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-5 shrink-0">
              {index + 1}.
            </span>
            <Input
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
              disabled={disabled}
              className="flex-1"
              data-testid={`${testIdPrefix}-item-${index}`}
            />
            {!disabled && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeItem(index)}
                data-testid={`${testIdPrefix}-remove-${index}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <div className="flex items-center gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addItem();
              }
            }}
            data-testid={`${testIdPrefix}-new-input`}
          />
          <Button
            size="icon"
            variant="outline"
            onClick={addItem}
            disabled={!newItem.trim()}
            data-testid={`${testIdPrefix}-add`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export function RecipeVariantFields({
  variant,
  onFieldChange,
  onFileUpload,
  isUploading,
  isReadOnly = false,
}: RecipeVariantFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Title</Label>
        <Input
          value={variant.localizedTitle}
          onChange={(e) => onFieldChange("localizedTitle", e.target.value)}
          placeholder="Enter recipe title"
          disabled={isReadOnly}
          data-testid="input-variant-title"
        />
      </div>

      <div className="space-y-2">
        <Label>Short Description</Label>
        <Textarea
          value={variant.localizedDescription}
          onChange={(e) =>
            onFieldChange("localizedDescription", e.target.value)
          }
          placeholder="Brief tagline shown in cards and search"
          rows={2}
          maxLength={150}
          disabled={isReadOnly}
          data-testid="input-variant-description"
        />
        <p className="text-xs text-muted-foreground text-right">
          {variant.localizedDescription?.length ?? 0}/150
        </p>
      </div>

      <div className="space-y-2">
        <Label>About (Long Description)</Label>
        <Textarea
          value={variant.aboutLongDescription ?? ""}
          onChange={(e) =>
            onFieldChange("aboutLongDescription", e.target.value)
          }
          placeholder="Longer 'About' copy shown on the recipe detail page"
          rows={5}
          disabled={isReadOnly}
          data-testid="input-variant-about"
        />
      </div>

      <SimpleListEditor
        label="Ingredients"
        items={variant.localizedIngredients}
        onChange={(items) => onFieldChange("localizedIngredients", items)}
        placeholder="Add an ingredient"
        disabled={isReadOnly}
        testIdPrefix="ingredient"
      />

      <SortableStepsList
        label="Steps"
        items={variant.localizedSteps}
        onChange={(items) => onFieldChange("localizedSteps", items)}
        placeholder="Add a step"
        disabled={isReadOnly}
        testIdPrefix="step"
      />

      <SimpleListEditor
        label="User Tips / Notes"
        items={variant.userTips ?? []}
        onChange={(items) => onFieldChange("userTips", items)}
        placeholder="Add a helpful tip"
        disabled={isReadOnly}
        testIdPrefix="tip"
      />

      <div className="space-y-2">
        <Label>Thumbnail</Label>
        {variant.thumbnailUrl ? (
          <MediaPreview
            url={variant.thumbnailUrl}
            mediaType="image"
            onRemove={
              isReadOnly ? undefined : () => onFieldChange("thumbnailUrl", null)
            }
            onReplace={
              isReadOnly ? undefined : (e) => onFileUpload(e, "thumbnailUrl")
            }
            replaceAccept={THUMBNAIL_ACCEPT_ATTR}
            isReadOnly={isReadOnly}
          />
        ) : (
          <UploadDropzone
            type="image"
            label="Upload Thumbnail"
            hint={THUMBNAIL_HINT}
            accept={THUMBNAIL_ACCEPT_ATTR}
            onChange={(e) => onFileUpload(e, "thumbnailUrl")}
            isUploading={isUploading}
            disabled={isReadOnly}
            testId="button-upload-thumbnail"
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Prep Time (minutes)</Label>
          <Input
            type="number"
            min={0}
            value={variant.prepTimeMinutes ?? ""}
            onChange={(e) =>
              onFieldChange(
                "prepTimeMinutes",
                e.target.value ? Number(e.target.value) : null,
              )
            }
            placeholder="e.g. 15"
            disabled={isReadOnly}
            data-testid="input-variant-prep-time"
          />
        </div>
        <div className="space-y-2">
          <Label>Cook Time (minutes)</Label>
          <Input
            type="number"
            min={0}
            value={variant.cookTimeMinutes ?? ""}
            onChange={(e) =>
              onFieldChange(
                "cookTimeMinutes",
                e.target.value ? Number(e.target.value) : null,
              )
            }
            placeholder="e.g. 30"
            disabled={isReadOnly}
            data-testid="input-variant-cook-time"
          />
        </div>
      </div>
    </div>
  );
}
